import { MapPin, Plus, Edit3, Trash2, Home, Building, User, CheckCircle, Truck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { useAddress } from '@/context/AddressContext';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Address } from '@/context/AddressContext';

const AddressManagementPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectMode = searchParams.get('selectMode') === 'true';

  const {
    addresses,
    loading,
    refreshAddresses,
    deleteAddress,
    setDefaultAddress,
  } = useAddress();

  const { user } = useAuth();
  const { toast } = useToast();
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshAddresses();
    setRefreshing(false);
  };

  const handleAddNewAddress = () => {
    navigate('/address/add');
  };

  const handleEditAddress = (address: Address) => {
    navigate('/address/edit', { state: { address } });
  };

  const handleDeleteAddress = async (addressId: number, fullName: string) => {
    if (window.confirm(`Are you sure you want to delete the address for ${fullName}?`)) {
      try {
        await deleteAddress(addressId);
        toast({
          title: 'Success',
          description: 'Address deleted successfully',
        });
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to delete address',
          variant: 'destructive',
        });
      }
    }
  };

  const handleSetDefault = async (addressId: number, fullName: string) => {
    try {
      await setDefaultAddress(addressId);
      toast({
        title: 'Success',
        description: `${fullName}'s address set as default`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Unable to set default address',
        variant: 'destructive',
      });
    }
  };

  const handleSelectAddress = async (address: Address) => {
    if (!selectMode) return;

    try {
      await setDefaultAddress(address.addressId);
      navigate(-1); // Go back to previous page
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Unable to select address. Try again.',
        variant: 'destructive',
      });
    }
  };

  const getAddressTypeIcon = (type: string) => {
    switch (type) {
      case 'HOME':
        return Home;
      case 'WORK':
        return Building;
      case 'OTHER':
      default:
        return User;
    }
  };

  const getAddressTypeColor = (type: string) => {
    switch (type) {
      case 'HOME':
        return 'text-green-600';
      case 'WORK':
        return 'text-blue-600';
      case 'OTHER':
      default:
        return 'text-gray-600';
    }
  };

  // Show authentication message if user is not authenticated
  if (!user?.isAuthenticated || !user?.customerId) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center">
            <MapPin className="h-10 w-10 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Sign in to manage addresses</h1>
          <p className="text-muted-foreground mb-8">Please sign in with your account to manage your delivery addresses.</p>
          <Link to="/auth/signin">
            <Button variant="cta">Sign In</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  if (loading && addresses.length === 0) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="w-8 h-8 mx-auto mb-4 border-4 border-primary border-r-transparent animate-spin rounded-full"></div>
          <p className="text-muted-foreground">Loading addresses...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold">
            {selectMode ? 'Select Delivery Address' : 'Saved Addresses'}
          </h1>
          <Button 
            onClick={handleAddNewAddress}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Address
          </Button>
        </div>

        {addresses.length === 0 ? (
          // Empty State
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center">
              <MapPin className="h-10 w-10 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold mb-4">No addresses saved</h2>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              {selectMode
                ? 'Add your first delivery address'
                : 'Add your first delivery address to get started'}
            </p>
            <Button 
              onClick={handleAddNewAddress}
              variant="cta"
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Address
            </Button>
          </div>
        ) : (
          // Address List
          <div className="space-y-4 md:space-y-6">
            <AnimatePresence>
              {addresses.map((address, index) => {
                const AddressIcon = getAddressTypeIcon(address.addressType);
                const iconColor = getAddressTypeColor(address.addressType);

                return (
                  <motion.div
                    key={address.addressId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.1 }}
                    className={`bg-card rounded-xl shadow-card p-4 md:p-6 border-2 transition-all ${
                      address.isDefault
                        ? 'border-primary bg-primary/5'
                        : 'border-transparent hover:border-primary/20'
                    }`}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full bg-secondary flex items-center justify-center ${iconColor}`}>
                          <AddressIcon className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-base">{address.addressType}</h3>
                            {address.isDefault && (
                              <span className="bg-primary text-white text-xs px-2 py-1 rounded-full">
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{address.fullName}</p>
                        </div>
                      </div>

                      {/* Actions */}
                      {selectMode ? (
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          address.isDefault 
                            ? 'bg-primary border-primary' 
                            : 'border-border hover:border-primary'
                        }`}>
                          {address.isDefault && <CheckCircle className="h-4 w-4 text-white" />}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditAddress(address)}
                            className="text-primary hover:text-primary hover:bg-primary/10"
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAddress(address.addressId, address.fullName)}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Address Details */}
                    <div className="space-y-1 mb-4">
                      <p className="text-sm">
                        {address.addressLine1}
                        {address.addressLine2 && `, ${address.addressLine2}`}
                      </p>
                      <p className="text-sm">
                        {address.city}, {address.state} - {address.postalCode}
                      </p>
                      <p className="text-sm">{address.country}</p>
                      <p className="text-sm font-medium">Phone: {address.phoneNumber}</p>
                      
                      {address.deliveryInstructions && (
                        <p className="text-sm text-muted-foreground mt-2">
                          <strong>Note:</strong> {address.deliveryInstructions}
                        </p>
                      )}
                    </div>

                    {/* Action Buttons */}
                    {selectMode ? (
                      <Button
                        onClick={() => handleSelectAddress(address)}
                        disabled={address.isDefault}
                        className={`w-full ${
                          address.isDefault 
                            ? 'bg-green-600 hover:bg-green-600 text-white' 
                            : 'bg-primary hover:bg-primary/90 text-white'
                        }`}
                      >
                        {address.isDefault ? (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Selected for Delivery
                          </>
                        ) : (
                          <>
                            <Truck className="h-4 w-4 mr-2" />
                            Deliver Here
                          </>
                        )}
                      </Button>
                    ) : (
                      !address.isDefault && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSetDefault(address.addressId, address.fullName)}
                          className="text-primary border-primary hover:bg-primary hover:text-white"
                        >
                          Set as Default
                        </Button>
                      )
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {/* Refresh Button */}
        {addresses.length > 0 && (
          <div className="text-center mt-8">
            <Button
              variant="ghost"
              onClick={handleRefresh}
              disabled={refreshing}
              className="text-primary hover:bg-primary/10"
            >
              {refreshing ? (
                <div className="w-4 h-4 border-2 border-primary border-r-transparent animate-spin rounded-full mr-2"></div>
              ) : (
                <MapPin className="h-4 w-4 mr-2" />
              )}
              {refreshing ? 'Refreshing...' : 'Refresh Addresses'}
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AddressManagementPage;